import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, CheckCircle2, XCircle, Beaker, Clock, AlertCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const QCPass = () => {
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();
    const { activeBranchId } = UseAuth();

    // 1. FETCH PENDING BATCHES
    const { data: pendingBatches = [], isLoading } = useQuery({
        queryKey: ['qc-pending', activeBranchId],
        queryFn: async () => {
            const res = await axiosSecure.get('/qc/pending');
            return res.data?.data || [];
        },
        enabled: !!activeBranchId
    });

    // 2. REVIEW MUTATION (Pass or Fail)
    const reviewMutation = useMutation({
        mutationFn: async (reviewData) => {
            const res = await axiosSecure.put('/qc/review', reviewData);
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['qc-pending', activeBranchId]);
            queryClient.invalidateQueries(['products-list', activeBranchId]); // Refresh stock if passed!
        },
        onError: (error) => {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to update QC status', background: '#1a103c', color: '#fff' });
        }
    });

    // 3. HANDLE PASS
    const handlePass = (batchId) => {
        Swal.fire({
            title: 'Approve Batch?',
            text: "This will instantly add the produced quantity to your sellable stock.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981', // Emerald
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Pass & Stock It!',
            background: '#1a103c',
            color: '#fff'
        }).then((result) => {
            if (result.isConfirmed) {
                reviewMutation.mutate({ batchId, status: 'Passed' });
                Swal.fire({ title: 'Passed!', text: 'Stock updated.', icon: 'success', timer: 1500, showConfirmButton: false, background: '#1a103c', color: '#fff' });
            }
        });
    };

    // 4. HANDLE FAIL
    const handleFail = (batchId) => {
        Swal.fire({
            title: 'Reject Batch?',
            input: 'textarea',
            inputLabel: 'Reason for failure (e.g., Bad texture, burnt, wrong ratio)',
            inputPlaceholder: 'Type your notes here...',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444', // Red
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Fail & Mark as Waste',
            background: '#1a103c',
            color: '#fff',
            inputValidator: (value) => {
                if (!value) return 'You must provide a reason for rejecting the batch!';
            }
        }).then((result) => {
            if (result.isConfirmed) {
                reviewMutation.mutate({ batchId, status: 'Failed', qcNotes: result.value });
                Swal.fire({ title: 'Rejected', text: 'Batch marked as waste.', icon: 'info', timer: 1500, showConfirmButton: false, background: '#1a103c', color: '#fff' });
            }
        });
    };

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading QC Dashboard...</div>;

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            
            {/* --- HEADER --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <ClipboardCheck className="text-pink-500" /> QC Approval Board
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Review newly produced batches before they hit the storefront.</p>
                </div>
                <div className="bg-orange-500/10 text-orange-400 px-4 py-2 rounded-xl border border-orange-500/20 font-bold flex items-center gap-2">
                    <AlertCircle size={18} /> {pendingBatches.length} Pending Review
                </div>
            </div>

            {/* --- BATCHES GRID --- */}
            {isLoading ? (
                <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div></div>
            ) : pendingBatches.length === 0 ? (
                <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-12 text-center text-slate-500 flex flex-col items-center">
                    <CheckCircle2 size={64} className="mb-4 opacity-20 text-emerald-500" />
                    <h3 className="text-xl font-bold text-white mb-2">All Caught Up!</h3>
                    <p>There are no pending batches waiting for quality control.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {pendingBatches.map((batch) => (
                        <div key={batch._id} className="bg-[#1a103c]/60 backdrop-blur-xl border border-orange-500/30 rounded-3xl p-6 shadow-xl relative overflow-hidden">
                            
                            {/* Top Accent */}
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-pink-500"></div>

                            <div className="flex justify-between items-start mb-4">
                                <div className="bg-white/5 p-3 rounded-xl text-orange-400">
                                    <Beaker size={24} />
                                </div>
                                <span className="text-xs text-slate-400 flex items-center gap-1 bg-black/20 px-2 py-1 rounded-lg">
                                    <Clock size={12}/> {new Date(batch.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                            </div>

                            <h3 className="text-xl font-bold text-white mb-1 line-clamp-1">
                                {batch.recipeId?.recipeName || "Unknown Recipe"}
                            </h3>
                            <p className="text-sm text-pink-400 font-bold mb-6 uppercase tracking-wider">
                                {batch.category}
                            </p>
                            
                            <div className="bg-black/20 rounded-2xl p-4 mb-6 border border-white/5 text-center">
                                <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-1">Produced Quantity</p>
                                <p className="text-3xl font-black text-white">
                                    {batch.quantityProduced} <span className="text-sm text-slate-400 font-medium">{batch.recipeId?.yieldUnit}</span>
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button 
                                    onClick={() => handleFail(batch._id)}
                                    className="flex-1 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-bold rounded-xl border border-red-500/20 flex items-center justify-center gap-2 transition-colors"
                                >
                                    <XCircle size={18} /> Reject
                                </button>
                                <button 
                                    onClick={() => handlePass(batch._id)}
                                    className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 transition-all active:scale-95"
                                >
                                    <CheckCircle2 size={18} /> Pass QC
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default QCPass;